import { useState, useEffect } from 'react';
import { useChartColors } from '@/hooks/useChartColors';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { TrendingUp, Target, Calendar, AlertTriangle, HelpCircle } from 'lucide-react';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer,
  ReferenceLine,
  Legend
} from 'recharts';
import { format, subMonths, startOfMonth, endOfMonth, parseISO, addMonths } from 'date-fns';

interface MonthlyData {
  month: string;
  monthLabel: string;
  savings: number;
  cases: number;
  isProjected: boolean;
}

interface CostDefaults {
  HOME: number;
  PCP: number;
  URGENT_CARE: number;
  ER_NOW: number;
}

type RecommendationType = 'HOME' | 'PCP' | 'URGENT_CARE' | 'ER_NOW';

interface CostSavingsForecastProps {
  agencyId: string | null;
  customHospitalCost?: number | null;
  selectedRecommendations?: RecommendationType[];
  patientIds?: string[];
}

type ForecastPeriod = '3' | '6' | '12';

export default function CostSavingsForecast({ 
  agencyId, 
  customHospitalCost, 
  selectedRecommendations = ['HOME', 'PCP', 'URGENT_CARE', 'ER_NOW'],
  patientIds
}: CostSavingsForecastProps) {
  const chartColors = useChartColors();
  const [forecastPeriod, setForecastPeriod] = useState<ForecastPeriod>('6');
  const [data, setData] = useState<MonthlyData[]>([]);
  const [costDefaults, setCostDefaults] = useState<CostDefaults>({
    HOME: 150,
    PCP: 450,
    URGENT_CARE: 500,
    ER_NOW: 2500
  });
  const [loading, setLoading] = useState(true);
  const [trendInfo, setTrendInfo] = useState({ 
    monthlyGrowthRate: 0, 
    confidence: 'low' as 'low' | 'medium' | 'high',
    projectedTotal: 0,
    historicalAvg: 0
  });

  useEffect(() => {
    loadCostDefaults();
  }, []);

  useEffect(() => {
    loadForecastData();
  }, [agencyId, forecastPeriod, costDefaults, customHospitalCost, selectedRecommendations]);

  const loadCostDefaults = async () => {
    try {
      const { data, error } = await supabase
        .from('hospital_visit_cost_defaults')
        .select('recommendation_level, default_cost');

      if (error) throw error;

      if (data) {
        const defaults: CostDefaults = { HOME: 150, PCP: 450, URGENT_CARE: 500, ER_NOW: 2500 };
        data.forEach((row: { recommendation_level: string; default_cost: number }) => {
          if (row.recommendation_level in defaults) {
            defaults[row.recommendation_level as keyof CostDefaults] = row.default_cost;
          }
        });
        setCostDefaults(defaults);
      }
    } catch (error) {
      console.error('Error loading cost defaults:', error);
    }
  };

  const calculateLinearRegression = (points: { x: number; y: number }[]) => {
    const n = points.length;
    if (n < 2) return { slope: 0, intercept: 0, r2: 0 };

    const sumX = points.reduce((a, b) => a + b.x, 0);
    const sumY = points.reduce((a, b) => a + b.y, 0);
    const sumXY = points.reduce((a, b) => a + b.x * b.y, 0);
    const sumX2 = points.reduce((a, b) => a + b.x * b.x, 0);
    const sumY2 = points.reduce((a, b) => a + b.y * b.y, 0);

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    // Calculate R-squared
    const yMean = sumY / n;
    const ssTotal = points.reduce((a, b) => a + Math.pow(b.y - yMean, 2), 0);
    const ssResidual = points.reduce((a, b) => a + Math.pow(b.y - (slope * b.x + intercept), 2), 0);
    const r2 = ssTotal > 0 ? 1 - ssResidual / ssTotal : 0;

    return { slope, intercept, r2 };
  };

  const loadForecastData = async () => {
    setLoading(true);
    try {
      const historicalMonths = 12;
      const startDate = startOfMonth(subMonths(new Date(), historicalMonths - 1));
      
      let query = supabase
        .from('triage_sessions')
        .select('recommendation, completed_at')
        .not('completed_at', 'is', null)
        .gte('completed_at', startDate.toISOString())
        .order('completed_at', { ascending: true });
      
      if (agencyId) {
        query = query.eq('agency_id', agencyId);
      }
      
      const { data: sessions, error } = await query;

      if (error) throw error;

      const effectiveErCost = customHospitalCost || costDefaults.ER_NOW;

      // Group sessions by month
      const monthlyMap = new Map<string, { savings: number; cases: number }>();
      
      // Initialize historical months
      for (let i = historicalMonths - 1; i >= 0; i--) {
        const monthDate = subMonths(new Date(), i);
        const monthKey = format(monthDate, 'yyyy-MM');
        monthlyMap.set(monthKey, { savings: 0, cases: 0 });
      }

      // Process sessions - filter by selected recommendations
      (sessions || []).forEach(session => {
        if (!session.completed_at) return;
        if (!selectedRecommendations.includes(session.recommendation as RecommendationType)) return;
        
        const monthKey = format(parseISO(session.completed_at), 'yyyy-MM');
        const monthData = monthlyMap.get(monthKey);
        
        if (monthData) {
          monthData.cases++;
          switch (session.recommendation) {
            case 'HOME':
              monthData.savings += effectiveErCost - costDefaults.HOME;
              break;
            case 'PCP':
              monthData.savings += effectiveErCost - costDefaults.PCP;
              break;
            case 'URGENT_CARE':
              monthData.savings += effectiveErCost - costDefaults.URGENT_CARE;
              break;
          }
        }
      });

      // Convert to array for regression
      const historicalData: MonthlyData[] = [];
      const regressionPoints: { x: number; y: number }[] = [];
      let index = 0;

      for (let i = historicalMonths - 1; i >= 0; i--) {
        const monthDate = subMonths(new Date(), i);
        const monthKey = format(monthDate, 'yyyy-MM');
        const monthData = monthlyMap.get(monthKey)!;
        
        historicalData.push({
          month: monthKey,
          monthLabel: format(monthDate, 'MMM yyyy'),
          savings: monthData.savings,
          cases: monthData.cases,
          isProjected: false
        });

        regressionPoints.push({ x: index, y: monthData.savings });
        index++;
      }

      // Calculate trend using linear regression
      const { slope, intercept, r2 } = calculateLinearRegression(regressionPoints);
      
      // Determine confidence level based on R-squared and data points with activity
      const activeMonths = historicalData.filter(m => m.savings > 0).length;
      let confidence: 'low' | 'medium' | 'high' = 'low';
      if (activeMonths >= 6 && r2 > 0.5) confidence = 'high';
      else if (activeMonths >= 3 && r2 > 0.3) confidence = 'medium';

      // Calculate monthly growth rate
      const historicalAvg = historicalData.reduce((sum, m) => sum + m.savings, 0) / historicalMonths;
      const monthlyGrowthRate = historicalAvg > 0 ? (slope / historicalAvg) * 100 : 0;

      // Generate forecast
      const forecastMonths = parseInt(forecastPeriod);
      const projectedData: MonthlyData[] = [];
      let projectedTotal = 0;

      for (let i = 1; i <= forecastMonths; i++) {
        const monthDate = addMonths(new Date(), i);
        const projectedValue = Math.max(0, slope * (historicalMonths - 1 + i) + intercept);
        projectedTotal += projectedValue;

        projectedData.push({
          month: format(monthDate, 'yyyy-MM'),
          monthLabel: format(monthDate, 'MMM yyyy'),
          savings: projectedValue,
          cases: 0, // Projected cases not shown
          isProjected: true
        });
      }

      setData([...historicalData, ...projectedData]);
      setTrendInfo({
        monthlyGrowthRate,
        confidence,
        projectedTotal,
        historicalAvg
      });
    } catch (error) {
      console.error('Error loading forecast data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload as MonthlyData;
      return (
        <div 
          className="rounded-lg shadow-lg p-3 border bg-white"
          style={chartColors.tooltipStyle}
        >
          <p className="font-medium mb-1 text-gray-900">{label}</p>
          {dataPoint.isProjected && (
            <Badge variant="secondary" className="mb-2 text-xs">Projected</Badge>
          )}
          <div className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: dataPoint.isProjected ? '#8B5CF6' : '#10B981' }} />
            <span className="text-gray-600">Savings:</span>
            <span className="font-medium text-gray-900">{formatCurrency(dataPoint.savings)}</span>
          </div>
          {!dataPoint.isProjected && dataPoint.cases > 0 && (
            <div className="flex items-center gap-2 text-sm mt-1">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <span className="text-gray-600">Cases:</span>
              <span className="font-medium text-gray-900">{dataPoint.cases}</span>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  const getConfidenceBadge = () => {
    const colors = {
      low: 'bg-orange-100 text-orange-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-green-100 text-green-800'
    };
    return (
      <Badge variant="secondary" className={colors[trendInfo.confidence]}>
        {trendInfo.confidence.charAt(0).toUpperCase() + trendInfo.confidence.slice(1)} Confidence
      </Badge>
    );
  };

  // Find the split point between historical and projected
  const splitIndex = data.findIndex(d => d.isProjected);
  const historicalData = data.filter(d => !d.isProjected);
  const projectedData = data.filter(d => d.isProjected);

  if (loading) {
    return (
      <Card className={`${chartColors.cardClassName} shadow-sm`}>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Target className="h-5 w-5" />
            <span>Savings Forecast</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse h-64 bg-muted rounded"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`${chartColors.cardClassName} shadow-sm`}>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle className="flex items-center space-x-2">
              <Target className="h-5 w-5 text-purple-600" />
              <span>Savings Forecast</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xs">
                    <p className="text-sm">Uses all historical data to project future savings. Has its own forecast period selector. Not affected by the date filter above.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardTitle>
            <CardDescription>
              Projected savings based on historical trends
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            {getConfidenceBadge()}
            <Select value={forecastPeriod} onValueChange={(v) => setForecastPeriod(v as ForecastPeriod)}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="3">3 Months</SelectItem>
                <SelectItem value="6">6 Months</SelectItem>
                <SelectItem value="12">12 Months</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="p-4 rounded-lg bg-muted/50 border border-border">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Monthly Trend</span>
            </div>
            <p className={`text-xl font-bold ${trendInfo.monthlyGrowthRate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trendInfo.monthlyGrowthRate >= 0 ? '+' : ''}{trendInfo.monthlyGrowthRate.toFixed(1)}%
            </p>
          </div>

          <div className="p-4 rounded-lg bg-muted/50 border border-border">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Historical Avg</span>
            </div>
            <p className="text-xl font-bold text-foreground">
              {formatCurrency(trendInfo.historicalAvg)}
            </p>
          </div>

          <div className="p-4 rounded-lg bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800">
            <div className="flex items-center gap-2 mb-1">
              <Target className="h-4 w-4 text-purple-600" />
              <span className="text-sm text-purple-700 dark:text-purple-300">Projected ({forecastPeriod}mo)</span>
            </div>
            <p className="text-xl font-bold text-purple-600">
              {formatCurrency(trendInfo.projectedTotal)}
            </p>
          </div>

          <div className="p-4 rounded-lg bg-muted/50 border border-border">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Data Points</span>
            </div>
            <p className="text-xl font-bold text-foreground">
              {historicalData.filter(d => d.savings > 0).length} months
            </p>
          </div>
        </div>

        {/* Confidence Warning */}
        {trendInfo.confidence === 'low' && (
          <div className="flex items-start gap-3 p-4 rounded-lg bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800">
            <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-orange-800 dark:text-orange-200">Limited Historical Data</p>
              <p className="text-sm text-orange-700 dark:text-orange-300">
                Forecast accuracy improves with more historical data. Current projections are based on limited trends.
              </p>
            </div>
          </div>
        )}

        {/* Chart */}
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorHistorical" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.4} />
                  <stop offset="50%" stopColor="#34D399" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="#6EE7B7" stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="colorProjected" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.4} />
                  <stop offset="50%" stopColor="#A78BFA" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="#C4B5FD" stopOpacity={0.05} />
                </linearGradient>
                <filter id="forecastGlow">
                  <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
              <XAxis 
                dataKey="monthLabel" 
                tick={{ fontSize: 11, fill: chartColors.axis }}
                tickLine={false}
                axisLine={false}
                interval="preserveStartEnd"
              />
              <YAxis 
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                tick={{ fontSize: 12, fill: chartColors.axis }}
                tickLine={false}
                axisLine={false}
              />
              <RechartsTooltip content={<CustomTooltip />} />
              <Legend 
                formatter={(value) => (
                  <span className="text-sm text-muted-foreground">
                    {value === 'Historical' ? 'Historical Savings' : 'Projected Savings'}
                  </span>
                )}
              />
              {splitIndex > 0 && (
                <ReferenceLine 
                  x={data[splitIndex - 1]?.monthLabel} 
                  stroke="#6B7280" 
                  strokeDasharray="3 3"
                  label={{ value: 'Now', position: 'top', fontSize: 11, fill: '#6B7280' }}
                />
              )}
              <Area 
                type="monotone" 
                dataKey={(d: MonthlyData) => !d.isProjected ? d.savings : null}
                name="Historical"
                stroke="#10B981" 
                strokeWidth={3}
                fillOpacity={1} 
                fill="url(#colorHistorical)"
                connectNulls={false}
                dot={{ fill: '#10B981', strokeWidth: 2, stroke: '#fff', r: 4 }}
                activeDot={{ r: 8, strokeWidth: 2, stroke: '#fff', fill: '#10B981', filter: 'url(#forecastGlow)' }}
                animationDuration={1500}
                animationEasing="ease-out"
              />
              <Area 
                type="monotone" 
                dataKey={(d: MonthlyData) => d.isProjected ? d.savings : (splitIndex > 0 && data.indexOf(d) === splitIndex - 1 ? d.savings : null)}
                name="Projected"
                stroke="#8B5CF6" 
                strokeWidth={3}
                strokeDasharray="8 4"
                fillOpacity={1} 
                fill="url(#colorProjected)"
                connectNulls
                dot={{ fill: '#8B5CF6', strokeWidth: 2, stroke: '#fff', r: 4 }}
                activeDot={{ r: 8, strokeWidth: 2, stroke: '#fff', fill: '#8B5CF6', filter: 'url(#forecastGlow)' }}
                animationDuration={1500}
                animationEasing="ease-out"
                animationBegin={500}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Methodology Note */}
        <p className="text-xs text-muted-foreground text-center">
          Forecast uses linear regression on the last 12 months of data. Actual results may vary based on usage patterns.
        </p>
      </CardContent>
    </Card>
  );
}
